import { MockResponse } from './mockResponses';

// Helper function to create a mock Response object
export function createMockResponse(mockResponse: MockResponse): Response {
  const { status, data, headers = {} } = mockResponse;
  
  // Determine if data should be JSON or text
  const isJson = typeof data === 'object' && data !== null;
  const body = isJson ? JSON.stringify(data) : String(data);
  
  // Set default headers
  const responseHeaders = new Headers({
    'Content-Type': isJson ? 'application/json' : 'text/plain',
    ...headers
  });

  return new Response(body, {
    status,
    statusText: status === 200 ? 'OK' : 'Error',
    headers: responseHeaders
  });
}

// Helper function to match URL patterns
export function matchEndpoint(url: string, pattern: string): boolean {
  // ignore query params
  const urlWithoutQueryParams = url.split("?")[0];

  // Convert pattern like "*/customers/:token/kyc" to regex
  const regexPattern = pattern
    .replace(/\*/g, ".*")
    .replace(/:([^/]+)/g, "([^/]+)")
    .replace(/\//g, "\\/");

  const regex = new RegExp(`^${regexPattern}$`);
  return regex.test(urlWithoutQueryParams);
}

// Extract parameters from URL using pattern
export function extractParams(
  url: string,
  pattern: string
): Record<string, string> {
  const params: Record<string, string> = {};
  const patternParts = pattern.split("/");
  const urlParts = url.split("/");

  patternParts.forEach((part, index) => {
    if (part.startsWith(":")) {
      const paramName = part.slice(1);
      params[paramName] = urlParts[index] || "";
    }
  });

  return params;
}

// Check if mocking is enabled
export function isMockingEnabled(): boolean {
  return (
    process.env.GATSBY_MOCK_REQUESTS === 'true' || 
    process.env.MOCK_REQUESTS === 'true' ||
    (typeof window !== 'undefined' && (window as any).__MOCK_REQUESTS === true)
  );
}

// Parse request body for mock matching
function parseRequestBody(body: BodyInit | null | undefined): any {
  if (!body) return undefined;

  try {
    if (typeof body === 'string') {
      return JSON.parse(body);
    } else if (body instanceof FormData) {
      // For FormData, we'll just pass it as is
      return body;
    }
  } catch {
    // If parsing fails, return the body as is
    return body;
  }

  return body;
}

// Handle mock request processing
export async function handleMockRequest(
  url: string, 
  method: string, 
  init: RequestInit = {}
): Promise<Response | null> {
  console.log(`🔍 Checking mock for ${method} ${url}`);
  
  // Parse request body for mock matching
  const requestBody = parseRequestBody(init.body);

  // Import here to avoid circular dependency
  const { getMockResponse } = await import('./mockResponses');
  const mockResponse = getMockResponse(url, method, requestBody);
  
  if (mockResponse) {
    console.log(`✅ Using mock response for ${method} ${url}`);
    
    // Apply delay if specified
    if (mockResponse.delay) {
      await new Promise(resolve => setTimeout(resolve, mockResponse.delay));
    }

    console.log('mockResponse', mockResponse);
    
    return createMockResponse(mockResponse);
  } else {
    console.warn(`⚠️ No mock found for ${method} ${url}, making real request`);
    return null;
  }
} 